// Typed message lookup. `t` is keyed on the namespace registry in `namespaces.ts` and on the key
// map generated from the `nl` catalogues, so an unknown namespace or key is a compile error.

import { catalogueFor } from './catalogs'
import { formatMessage, type MessageParams } from './format'
import type { Catalogs } from './generated/types'
import { type CatalogLocale, type LocaleFor, type Namespace, SOURCE_LOCALE } from './namespaces'

/** Every key the `nl` catalogue of `N` defines. */
export type MessageKey<N extends Namespace> = Extract<keyof Catalogs[N], string>

/** A `t` bound to one namespace and locale, as returned by {@link createT}. */
export type Translator<N extends Namespace> = (key: MessageKey<N>, params?: MessageParams) => string

/**
 * Translates `key` from `namespace`. Defaults to the source language, so a caller that has no
 * locale yet still renders real text instead of a key.
 */
export function t<N extends Namespace, K extends MessageKey<N>>(
  namespace: N,
  key: K,
  params?: MessageParams,
  locale?: LocaleFor<N>,
): string {
  const target: CatalogLocale = locale ?? SOURCE_LOCALE
  return formatMessage(lookup(namespace, target, key), params, target)
}

/** Binds a namespace and locale once, for components and templates that translate many keys. */
export function createT<N extends Namespace>(namespace: N, locale?: LocaleFor<N>): Translator<N> {
  return (key, params) => t(namespace, key, params, locale)
}

function lookup(namespace: Namespace, locale: CatalogLocale, key: string): string {
  const message = catalogueFor(namespace, locale)?.[key]
  if (message !== undefined) return message

  // ADR 0010: `en` is the only cross-language fallback, and only for `de`.
  if (locale === 'de') {
    const english = catalogueFor(namespace, 'en')?.[key]
    if (english !== undefined) return english
  }

  // Only reachable for a locale the namespace has no catalogue for, which the types rule out and
  // `check-catalogs` guards against. Returning the key keeps a document renderable.
  return catalogueFor(namespace, SOURCE_LOCALE)?.[key] ?? key
}
